import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthDepartments, type AuthDepartmentPerson } from '@/api/auth';
import {
  createPurchasePlan,
  downloadPurchaseTemplate,
  type PurchaseSettingItem,
} from '@/api/purchase';
import MonthPicker, {
  addMonthsValue,
  currentMonthValue,
  nextMonthValue,
} from '@/components/base/MonthPicker';
import SearchableSelect from '@/components/base/SearchableSelect';
import { useMember } from '@/hooks/useMember';
import UniqueSpecOrderItemsTable, {
  applyProductPickMergingSpec,
  ensureSingleTrailingEmpty,
  isEmptyOrderItem,
  patchOrderItemMergingSpec,
  UNLIMITED_SPEC_LABEL,
  upsertOrderItemBySpec,
  type OrderItem,
  type ProductPickPayload,
} from '@/pages/purchase-plan/components/UniqueSpecOrderItemsTable';
import DownloadTemplateModal, {
  type DownloadTemplateValues,
} from '@/pages/purchase-plan/components/DownloadTemplateModal';
import ProductSelector, { type SelectableProduct } from '@/pages/order/components/ProductSelector';
import ConfirmModal from '@/pages/orders/components/ConfirmModal';
import PasteCodesModal, {
  PASTE_MATCH_FAIL_LABEL,
  pasteLineToOrderItem,
  type PasteCodeLine,
  type PasteMissedItem,
} from '@/pages/proxy-order/components/PasteCodesModal';
import { isPurchasePlanManager } from '@/utils/auth';

/** 与后端 control_type 一致（接口字段名；业务上也称计划类型） */
export const PURCHASE_CONTROL_TYPES = [
  { value: 1, label: '全院限购', mode: 'goods' as const, needDept: false, needAccount: false },
  { value: 2, label: '科室限购', mode: 'goods' as const, needDept: true, needAccount: false },
  { value: 3, label: '个人限购', mode: 'goods' as const, needDept: true, needAccount: true },
] as const;

type ControlTypeValue = (typeof PURCHASE_CONTROL_TYPES)[number]['value'];

interface FormErrors {
  month?: string;
  controlType?: string;
  department?: string;
  account?: string;
  items?: string;
  file?: string;
}

function hasUnmatchedSpecRemark(remark?: string): boolean {
  const text = String(remark || '').trim();
  const label = PASTE_MATCH_FAIL_LABEL.spec;
  return text === label || text.startsWith(`${label}；`);
}

function buildGoodsItems(items: OrderItem[]): { items: PurchaseSettingItem[] } | { error: string } {
  const filled = items.filter((item) => !isEmptyOrderItem(item));
  if (filled.length === 0) {
    return { error: '请至少添加一件物资' };
  }
  const next: PurchaseSettingItem[] = [];
  for (const item of filled) {
    const specId = Number(item.specId);
    const goodsId = Number(item.productId);
    const quantity = Math.floor(Number(item.quantity));
    const name = String(item.name || '').trim();
    const hasSpec = Number.isFinite(specId) && specId > 0;
    if (hasUnmatchedSpecRemark(item.itemRemark)) {
      return { error: `物资「${name || '未命名'}」无法匹配规格，请重新选择后再提交` };
    }
    if (!Number.isFinite(goodsId) || goodsId <= 0) {
      const unmatched = /无法匹配/.test(item.itemRemark || '');
      return {
        error: unmatched
          ? `物资「${name || '未命名'}」未能匹配，请重新选择`
          : `物资「${name || '未命名'}」信息不完整`,
      };
    }
    if (!hasSpec && !String(item.productId || '').trim()) {
      return { error: `物资「${name || '未命名'}」缺少有效规格，请重新选择` };
    }
    if (!Number.isFinite(quantity) || quantity < 1) {
      return { error: `物资「${name}」计划数量须大于 0` };
    }
    next.push({
      goods_id: goodsId,
      /** 不限定规格必须显式传 0，避免后端按规格限购写入 */
      spec_id: hasSpec ? specId : 0,
      quantity,
      price: item.unitPrice,
      amount: Number((item.unitPrice * quantity).toFixed(2)),
      remark: item.itemRemark?.trim() || undefined,
    });
  }
  return { items: next };
}

export default function PurchasePlanCreate() {
  const navigate = useNavigate();
  const { member } = useMember();
  const canSelectHospitalWide = isPurchasePlanManager(member);
  /** 本月起可选，最晚为未来 3 个月；默认仍选下个月 */
  const minMonth = useMemo(() => currentMonthValue(), []);
  const maxMonth = useMemo(() => addMonthsValue(new Date(), 3), []);

  const [month, setMonth] = useState(() => nextMonthValue());
  const [controlType, setControlType] = useState<ControlTypeValue>(2);
  const [remark, setRemark] = useState('');

  const [deptOptions, setDeptOptions] = useState<string[]>([]);
  const [deptIdMap, setDeptIdMap] = useState<Record<string, string>>({});
  const [personsByDept, setPersonsByDept] = useState<Record<string, AuthDepartmentPerson[]>>({});
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedPerson, setSelectedPerson] = useState('');

  const [goodsItems, setGoodsItems] = useState<OrderItem[]>(() => ensureSingleTrailingEmpty([]));
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [showPasteCodes, setShowPasteCodes] = useState(false);
  const [lastItemError, setLastItemError] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [successFlowId, setSuccessFlowId] = useState<string | null>(null);

  const availableControlTypes = useMemo(
    () =>
      PURCHASE_CONTROL_TYPES.filter(
        (type) => type.value !== 1 || canSelectHospitalWide,
      ),
    [canSelectHospitalWide],
  );

  const controlMeta = useMemo(
    () => PURCHASE_CONTROL_TYPES.find((t) => t.value === controlType) || PURCHASE_CONTROL_TYPES[1],
    [controlType],
  );
  /** 仅科室限购 / 个人限购显示科室；全院限购不显示 */
  const needDept = controlMeta.needDept;
  const needAccount = controlMeta.needAccount;

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  useEffect(() => {
    if (!canSelectHospitalWide && controlType === 1) {
      setControlType(2);
    }
  }, [canSelectHospitalWide, controlType]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await getAuthDepartments();
        const ok = res.error === false || Number(res.error) === 0;
        if (cancelled || !ok || !Array.isArray(res.data)) return;

        const nextIdMap: Record<string, string> = {};
        const nextPersons: Record<string, AuthDepartmentPerson[]> = {};
        const nextOptions: string[] = [];
        res.data.forEach((item) => {
          const id = Number(item.id);
          const title = String(item.title || '').trim();
          if (!title || !Number.isFinite(id) || id <= 0 || nextIdMap[title]) return;
          nextIdMap[title] = String(id);
          nextOptions.push(title);
          nextPersons[title] = Array.isArray(item.persons)
            ? item.persons
                .map((p) => ({
                  id: p.id,
                  account: p.account,
                  name: String(p.name || p.account || '').trim(),
                  phone: p.phone,
                }))
                .filter((p) => !!p.name)
            : [];
        });
        setDeptIdMap(nextIdMap);
        setDeptOptions(nextOptions);
        setPersonsByDept(nextPersons);
      } catch {
        if (!cancelled) {
          setDeptOptions([]);
          setDeptIdMap({});
          setPersonsByDept({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const personOptions = useMemo(() => {
    if (!selectedDept) return [];
    return (personsByDept[selectedDept] || []).map((p) => p.name);
  }, [selectedDept, personsByDept]);

  const selectedPersonId = useMemo(() => {
    if (!selectedDept || !selectedPerson) return '';
    const hit = (personsByDept[selectedDept] || []).find((p) => p.name === selectedPerson);
    return hit ? String(hit.id) : '';
  }, [selectedDept, selectedPerson, personsByDept]);

  const handleControlTypeChange = (value: ControlTypeValue) => {
    if (value === 1 && !canSelectHospitalWide) return;
    setControlType(value);
    setErrors((prev) => ({
      ...prev,
      controlType: undefined,
      account: undefined,
      items: undefined,
    }));
  };

  const handleDeptChange = (value: string | string[]) => {
    const next = Array.isArray(value) ? value[0] || '' : value;
    setSelectedDept(next);
    const persons = next ? personsByDept[next] || [] : [];
    const first = persons[0]?.name || '';
    setSelectedPerson(needAccount ? first : '');
    setErrors((prev) => ({
      ...prev,
      department: next ? undefined : prev.department,
      account: first ? undefined : prev.account,
    }));
  };

  const handlePersonChange = (value: string | string[]) => {
    const next = Array.isArray(value) ? value[0] || '' : value;
    setSelectedPerson(next);
    setErrors((prev) => ({ ...prev, account: next ? undefined : prev.account }));
  };

  const handleUpdateQuantity = (index: number, quantity: number) => {
    setGoodsItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], quantity };
      return next;
    });
    setErrors((prev) => (prev.items ? { ...prev, items: undefined } : prev));
  };

  const handleUpdateRemark = (index: number, remarkText: string) => {
    setGoodsItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], itemRemark: remarkText };
      return next;
    });
  };

  const handleUpdateSpec = (
    index: number,
    specId: string,
    price: number,
    model: string,
    unit?: string,
  ) => {
    const normalizedSpecId = String(specId || '').trim();
    setGoodsItems((prev) => {
      const { items } = patchOrderItemMergingSpec(prev, index, {
        // 空字符串表示不限定规格，必须写入 patch 以清空原 specId
        specId: normalizedSpecId,
        unitPrice: price,
        spec: normalizedSpecId ? model : UNLIMITED_SPEC_LABEL,
        ...(unit ? { unit } : {}),
      });
      return items;
    });
  };

  const handleUpdateProduct = (index: number, product: ProductPickPayload) => {
    setGoodsItems((prev) => applyProductPickMergingSpec(prev, index, product).items);
    setErrors((prev) => (prev.items ? { ...prev, items: undefined } : prev));
  };

  const handleDeleteItem = (index: number) => {
    const target = goodsItems[index];
    const filledCount = goodsItems.filter((item) => !isEmptyOrderItem(item)).length;
    if (target && !isEmptyOrderItem(target) && filledCount <= 1) {
      setLastItemError(true);
      window.setTimeout(() => setLastItemError(false), 3000);
      return;
    }
    setGoodsItems((prev) => ensureSingleTrailingEmpty(prev.filter((_, i) => i !== index)));
  };

  const handleAddProduct = (product: SelectableProduct) => {
    const newItem: OrderItem = {
      productId: product.goodsId,
      name: product.name,
      quantity: 1,
      unitPrice: product.price,
      unit: product.unit,
      specId: product.id,
      spec: product.model || '',
      image: product.image || '',
      specOptions: [
        {
          id: product.id,
          name: product.model || '',
          price: product.price,
          unit: product.unit,
        },
      ],
    };
    const { items, merged } = upsertOrderItemBySpec(goodsItems, newItem);
    setGoodsItems(items);
    setErrors((prev) => (prev.items ? { ...prev, items: undefined } : prev));
    showToast(merged ? `已合并同规格「${product.name}」` : `已添加「${product.name}」`);
  };

  const handlePasteCodesConfirm = (lines: PasteCodeLine[], missed: PasteMissedItem[]) => {
    let next = goodsItems;
    let mergedCount = 0;
    let addedCount = 0;
    lines.forEach((line) => {
      const result = upsertOrderItemBySpec(next, pasteLineToOrderItem(line));
      next = result.items;
      if (result.merged) mergedCount += 1;
      else addedCount += 1;
    });
    setGoodsItems(next);
    setErrors((prev) => (prev.items ? { ...prev, items: undefined } : prev));
    const failCount = missed.length;
    const summary =
      mergedCount > 0
        ? `新增 ${addedCount} 项、合并 ${mergedCount} 项`
        : `已批量匹配加入 ${addedCount} 项物资`;
    if (failCount > 0) {
      showToast(`${summary}；${failCount} 项未匹配已加入清单，请查看物资备注`, 'error');
    } else {
      showToast(summary);
    }
  };

  const validate = (): FormErrors => {
    const next: FormErrors = {};
    if (!month || !/^\d{4}-\d{2}$/.test(month)) {
      next.month = '请选择计划月份';
    } else if (month < minMonth || month > maxMonth) {
      next.month = `只能提交本月至未来 3 个月的计划（${minMonth} ~ ${maxMonth}）`;
    }
    if (!availableControlTypes.some((t) => t.value === controlType)) {
      next.controlType = '请选择计划类型';
    } else if (controlType === 1 && !canSelectHospitalWide) {
      next.controlType = '仅采购计划管理员可选择全院限购';
    }
    if (needDept) {
      const deptId = selectedDept ? deptIdMap[selectedDept] : '';
      if (!selectedDept || !deptId) next.department = '请选择科室部门';
    }
    if (needAccount) {
      if (!selectedPerson || !selectedPersonId) {
        next.account = selectedDept ? '请选择限购账号' : '请先选择科室部门';
      }
    }
    if (uploadFile) {
      const name = uploadFile.name.toLowerCase();
      if (!/\.(xlsx|xls|csv)$/.test(name)) {
        next.file = '仅支持 xlsx / xls / csv 文件';
      }
    } else {
      const built = buildGoodsItems(goodsItems);
      if ('error' in built) next.items = built.error;
    }
    return next;
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const nextErrors = validate();
    setErrors(nextErrors);
    if (nextErrors.items) {
      setLastItemError(true);
      window.setTimeout(() => setLastItemError(false), 3000);
    }
    if (Object.keys(nextErrors).length > 0) {
      showToast(
        nextErrors.month ||
          nextErrors.controlType ||
          nextErrors.department ||
          nextErrors.account ||
          nextErrors.file ||
          nextErrors.items ||
          '请完善表单',
        'error',
      );
      return;
    }

    let items: PurchaseSettingItem[] | undefined;
    if (!uploadFile) {
      const built = buildGoodsItems(goodsItems);
      if ('error' in built) {
        setErrors({ items: built.error });
        showToast(built.error, 'error');
        return;
      }
      items = built.items;
    }

    setSubmitting(true);
    try {
      const res = await createPurchasePlan({
        month,
        control_type: controlType,
        department_id: needDept && selectedDept ? deptIdMap[selectedDept] : undefined,
        account_id: needAccount && selectedPersonId ? selectedPersonId : undefined,
        remark: remark.trim() || undefined,
        items,
        file: uploadFile || undefined,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '创建失败，请稍后重试', 'error');
        return;
      }
      const flowId = String(res.data?.flow_id || '');
      setSuccessFlowId(flowId || 'created');
      showToast(res.message || '采购计划创建成功');
    } catch {
      showToast('网络异常，创建失败', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetList = () => {
    setGoodsItems(ensureSingleTrailingEmpty([]));
    setUploadFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setErrors((prev) => ({ ...prev, items: undefined, file: undefined }));
  };

  const clearUploadFile = () => {
    setUploadFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setErrors((prev) => ({ ...prev, file: undefined }));
  };

  const handleTemplateFileChange = (file: File | null) => {
    setUploadFile(file);
    setErrors((prev) => ({ ...prev, file: undefined, items: undefined }));
  };

  const handleDownloadTemplate = async (values: DownloadTemplateValues) => {
    if (downloading) return;

    let departmentId: string | undefined;
    if (values.departmentTitle) {
      departmentId = deptIdMap[values.departmentTitle];
      if (!departmentId) {
        showToast('科室信息无效，请刷新后重试', 'error');
        return;
      }
    } else if (needDept && !canSelectHospitalWide) {
      showToast('请选择科室', 'error');
      return;
    }

    const hasFrom = !!values.startTime;
    const hasTo = !!values.endTime;
    if (hasFrom !== hasTo) {
      showToast('请同时选择起始日期和结束日期', 'error');
      return;
    }
    if (hasFrom && hasTo && values.startTime > values.endTime) {
      showToast('结束日期不能早于起始日期', 'error');
      return;
    }

    setDownloading(true);
    try {
      const res = await downloadPurchaseTemplate({
        department_id: departmentId,
        start_time: hasFrom ? values.startTime : undefined,
        end_time: hasTo ? values.endTime : undefined,
      });
      const ok = res.error === false || Number(res.error) === 0;
      const fileUrl = String(res.url || '').trim();
      if (!ok || !fileUrl) {
        showToast(res.message || res.msg || '模板下载失败', 'error');
        return;
      }
      const fromUrl = decodeURIComponent(fileUrl.split('/').pop()?.split('?')[0] || '');
      const filename =
        String(res.filename || '').trim() || fromUrl || '采购计划表模板.xlsx';
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
      link.rel = 'noopener';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setDownloadOpen(false);
      showToast(res.message || '模板已开始下载（计划数量已按所选统计周期预填）');
    } catch {
      showToast('模板下载失败，请稍后重试', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const paramField = (
    label: string,
    required: boolean,
    children: ReactNode,
    error?: string,
  ) => (
    <div className="flex flex-col gap-1 min-w-0">
      <div className="flex items-center gap-2 min-w-0">
        {required ? <span className="text-sm text-red-500 shrink-0">*</span> : <span className="w-2 shrink-0" />}
        <span className="text-sm text-foreground-700 shrink-0 whitespace-nowrap">{label}</span>
        <div className="min-w-[160px] flex-1 max-w-[220px]">{children}</div>
      </div>
      {error ? (
        <p className="text-xs text-red-600 pl-6 flex items-center gap-1">
          <i className="ri-error-warning-line" />
          {error}
        </p>
      ) : null}
    </div>
  );

  return (
    <div className="flex-1 bg-background-100">
      {toast && (
        <div
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-[130] px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${
            toast.type === 'success'
              ? 'bg-accent-500 text-background-50'
              : 'bg-red-500 text-white'
          }`}
        >
          <i
            className={
              toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
            }
          />
          {toast.message}
        </div>
      )}

      <div className="px-4 md:px-6 pb-4 md:pb-6 space-y-4">
        {/* 计划参数 */}
        <div className="bg-white rounded-xl border border-background-200 p-5">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-red-500 shrink-0">*</span>
                <span className="text-sm text-foreground-700 shrink-0 whitespace-nowrap">
                  计划类型：
                </span>
                <div className="flex flex-wrap gap-2 items-center">
                  {availableControlTypes.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      disabled={submitting}
                      onClick={() => handleControlTypeChange(type.value)}
                      className={`px-3 py-1.5 rounded-md text-xs border transition-colors whitespace-nowrap ${
                        controlType === type.value
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
              {errors.controlType ? (
                <p className="text-xs text-red-600 mt-1.5 pl-6 flex items-center gap-1">
                  <i className="ri-error-warning-line" />
                  {errors.controlType}
                </p>
              ) : null}
            </div>

            {paramField(
              '计划月份：',
              true,
              <MonthPicker
                value={month}
                onChange={(v) => {
                  setMonth(v);
                  setErrors((prev) => ({ ...prev, month: undefined }));
                }}
                minMonth={minMonth}
                maxMonth={maxMonth}
                invalid={!!errors.month}
                disabled={submitting}
              />,
              errors.month,
            )}

            {needDept
              ? paramField(
                  '科室部门：',
                  true,
                  <SearchableSelect
                    options={deptOptions}
                    value={selectedDept}
                    onChange={handleDeptChange}
                    placeholder="请选择科室"
                    allLabel="请选择科室"
                    searchPlaceholder="搜索科室..."
                    icon="ri-building-line"
                    invalid={!!errors.department}
                  />,
                  errors.department,
                )
              : null}

            {needAccount
              ? paramField(
                  '限购账号：',
                  true,
                  <SearchableSelect
                    options={personOptions}
                    value={selectedPerson}
                    onChange={handlePersonChange}
                    placeholder={selectedDept ? '请选择账号' : '请先选科室'}
                    allLabel="请选择账号"
                    searchPlaceholder="搜索账号..."
                    icon="ri-user-line"
                    invalid={!!errors.account}
                  />,
                  errors.account,
                )
              : null}

            <button
              type="button"
              onClick={() => navigate('/purchase-plan')}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-background-300 text-xs text-foreground-600 hover:bg-background-50 shrink-0 ml-auto"
            >
              <i className="ri-arrow-left-line" />
              返回列表
            </button>
          </div>
        </div>

        {/* 商品清单 / 模板上传 */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
          className="hidden"
          onChange={(e) => handleTemplateFileChange(e.target.files?.[0] || null)}
        />

        {uploadFile ? (
          <div className="bg-white rounded-xl border border-background-200 p-5 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h3 className="text-sm font-semibold text-foreground-900">计划表模板</h3>
                <p className="text-xs text-foreground-500 mt-0.5">
                  将按当前计划类型（{controlMeta.label}）导入模板中已填写计划数量的物资
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={submitting || downloading}
                  onClick={() => setDownloadOpen(true)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-background-300 text-xs text-foreground-700 hover:bg-background-50 disabled:opacity-50"
                >
                  <i className="ri-download-2-line" />
                  下载模板
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-background-300 text-xs text-foreground-700 hover:bg-background-50 disabled:opacity-50"
                >
                  <i className="ri-upload-2-line" />
                  重新选择
                </button>
              </div>
            </div>
            <div
              className={`rounded-lg border border-dashed px-4 py-4 ${
                errors.file
                  ? 'border-red-500 ring-1 ring-red-200 bg-red-50'
                  : 'border-background-300 bg-background-50'
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-foreground-800 truncate">{uploadFile.name}</p>
                  <p className="text-xs text-foreground-400 mt-0.5">
                    {(uploadFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={clearUploadFile}
                  className="text-xs text-foreground-500 hover:text-red-600 whitespace-nowrap"
                >
                  移除并改用手填
                </button>
              </div>
            </div>
            {errors.file ? (
              <p className="text-xs text-red-600 flex items-center gap-1">
                <i className="ri-error-warning-line" />
                {errors.file}
              </p>
            ) : null}
          </div>
        ) : (
          <UniqueSpecOrderItemsTable
            items={goodsItems}
            editMode
            onUpdateQuantity={handleUpdateQuantity}
            onUpdateSpec={handleUpdateSpec}
            onUpdateProduct={handleUpdateProduct}
            onUpdateRemark={handleUpdateRemark}
            onDeleteItem={handleDeleteItem}
            onAddItem={() => setShowProductSelector(true)}
            lastItemError={lastItemError || !!errors.items}
            hideTitle={false}
            headerActions={
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={submitting || downloading}
                  onClick={() => setDownloadOpen(true)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-background-300 text-xs text-foreground-700 hover:bg-background-50 disabled:opacity-50"
                >
                  <i className="ri-download-2-line" />
                  下载模板
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-background-300 text-xs text-foreground-700 hover:bg-background-50 disabled:opacity-50"
                >
                  <i className="ri-upload-2-line" />
                  上传计划表
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => setShowPasteCodes(true)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-background-300 text-xs text-foreground-700 hover:bg-background-50 disabled:opacity-50"
                >
                  <i className="ri-clipboard-line" />
                  批量匹配
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => setShowProductSelector(true)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-xs disabled:opacity-50"
                >
                  <i className="ri-add-line" />
                  添加物资
                </button>
              </div>
            }
            footerExtra={null}
          />
        )}

        {errors.items ? (
          <div className="px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-xs text-red-600 flex items-center gap-1.5">
            <i className="ri-error-warning-line" />
            {errors.items}
          </div>
        ) : null}

        <div className="bg-white rounded-xl border border-background-200 p-5 space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground-800 mb-2 block">计划备注</label>
            <textarea
              value={remark}
              disabled={submitting}
              onChange={(e) => setRemark(e.target.value.slice(0, 1000))}
              rows={3}
              maxLength={1000}
              placeholder="计划说明、特殊要求等（选填）"
              className="w-full px-3 py-2.5 rounded-lg border border-background-300 text-sm outline-none resize-none transition-colors focus:border-primary-400 disabled:bg-background-100"
            />
            <p className="text-xs text-foreground-400 mt-1.5 text-right">{remark.length}/1000</p>
          </div>
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              disabled={submitting}
              onClick={handleResetList}
              className="px-4 py-2 rounded-lg border border-background-300 text-sm text-foreground-600 hover:bg-background-100 whitespace-nowrap disabled:opacity-50"
            >
              {uploadFile ? '清空模板' : '清空清单'}
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={() => void handleSubmit()}
              className="px-5 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium disabled:opacity-50 whitespace-nowrap inline-flex items-center gap-1.5"
            >
              {submitting ? <i className="ri-loader-4-line animate-spin" /> : null}
              {submitting ? '提交中...' : '提交计划设置'}
            </button>
          </div>
        </div>
      </div>

      <ProductSelector
        isOpen={showProductSelector}
        onClose={() => setShowProductSelector(false)}
        onSelect={handleAddProduct}
        existingProductIds={[]}
      />

      <PasteCodesModal
        isOpen={showPasteCodes}
        onClose={() => setShowPasteCodes(false)}
        onConfirm={handlePasteCodesConfirm}
        allowUnlimitedSpec
      />

      <DownloadTemplateModal
        isOpen={downloadOpen}
        downloading={downloading}
        showDept={needDept || canSelectHospitalWide}
        deptOptional={canSelectHospitalWide}
        deptOptions={deptOptions}
        onCancel={() => {
          if (!downloading) setDownloadOpen(false);
        }}
        onConfirm={(values) => void handleDownloadTemplate(values)}
      />

      <ConfirmModal
        isOpen={!!successFlowId}
        title="计划设置成功"
        message="采购计划已提交。是否前往计划详情查看，或继续创建？"
        cancelLabel="继续创建"
        confirmLabel="查看详情"
        onCancel={() => {
          setSuccessFlowId(null);
          handleResetList();
          setRemark('');
        }}
        onConfirm={() => {
          const id = successFlowId;
          setSuccessFlowId(null);
          if (id && id !== 'created') navigate(`/purchase-plan/${id}`);
          else navigate('/purchase-plan');
        }}
      />
    </div>
  );
}
